import { DateTime } from 'luxon';
import { LadingInfo } from '../../work-orders/work-order-details/work-order.interfaces';
import {
  AppointmentValues,
  FreightValues,
  LadingPatch,
  PanelDetails,
  ReferenceValues,
  StatusValues,
} from './work-order-panel.models';

// Full Details sends these legacy appointment fields as Eastern wall-clock values,
// even when PostgreSQL serializes them with an offset. Keep that contract here.
const APPOINTMENT_STORAGE_ZONE = 'America/Toronto';

export function referenceValues(lading: LadingInfo): ReferenceValues {
  return {
    pickup_reference_number: lading.pickup_reference_number || '',
    delivery_reference_number: lading.delivery_reference_number || '',
    booking_number: lading.booking_number || '',
    release_number: lading.release_number || '',
  };
}

export function referencePatch(current: ReferenceValues, original: ReferenceValues): LadingPatch {
  const patch: LadingPatch = {};
  const fields = Object.keys(original) as Array<keyof ReferenceValues>;

  for (const field of fields) {
    if (current[field] === original[field]) continue;
    patch[field] = current[field] || null;
  }

  return patch;
}

export function freightValues(lading: LadingInfo): FreightValues {
  return {
    type_of_equipment: lading.type_of_equipment || '',
    carrier_trailer: lading.carrier_trailer || '',
    carrier_tractor: lading.carrier_tractor || '',
    seal_number: lading.seal_number || '',
    load_details: lading.load_details || '',
    load_special_info: lading.load_special_info || '',
  };
}

export function freightPatch(current: FreightValues, original: FreightValues): LadingPatch {
  const patch: LadingPatch = {};
  const fields = Object.keys(original) as Array<keyof FreightValues>;
  for (const field of fields) {
    if (current[field] === original[field]) continue;
    const value =
      field === 'carrier_trailer' ? current[field].toUpperCase().replace(/[^A-Z0-9]/g, '') : current[field];
    patch[field] = value || null;
  }
  return patch;
}

export function appointmentValues(details: PanelDetails): AppointmentValues {
  const lading = details.lading;
  const timezone =
    lading.appointment_timezone || details.consignee?.address?.[0]?.timezone_id || APPOINTMENT_STORAGE_ZONE;
  const start = readAppointmentTime(lading.appointment_start, timezone);
  const end = readAppointmentTime(lading.appointment_end, timezone);
  const date = start?.toISODate() || lading.appointment_date?.slice(0, 10) || '';

  return {
    date,
    start: start?.toFormat('HH:mm') || '',
    endDate: end?.toISODate() || date,
    end: end?.toFormat('HH:mm') || '',
    timezone,
  };
}

function readAppointmentTime(value: string | null | undefined, timezone: string): DateTime | null {
  if (!value) return null;
  const wallClock = value.replace('T', ' ').slice(0, 19);
  const parsed = DateTime.fromSQL(wallClock, { zone: APPOINTMENT_STORAGE_ZONE });
  const local = parsed.setZone(timezone);
  return local.isValid ? local : null;
}

function validTimezone(timezone: string): boolean {
  return !!timezone && DateTime.fromISO('2000-01-01', { zone: timezone }).isValid;
}

export function localDateTime(date: string, time: string, timezone: string): DateTime | null {
  if (!date || !time || !validTimezone(timezone)) return null;
  const value = `${date}T${time}`;
  const parsed = DateTime.fromISO(value, { zone: timezone });

  // Luxon moves nonexistent spring-forward times forward. Require the time the
  // user actually entered so we never silently save a different appointment.
  if (!parsed.isValid || parsed.toFormat("yyyy-MM-dd'T'HH:mm") !== value.slice(0, 16)) {
    return null;
  }
  return parsed;
}

export function appointmentValidation(values: AppointmentValues): string | null {
  if (!validTimezone(values.timezone)) return 'Choose a valid appointment timezone.';
  if (!values.date) {
    return values.start || values.end ? 'Enter an appointment date, or clear both times.' : null;
  }
  if (!DateTime.fromISO(values.date).isValid) return 'Enter a valid appointment date.';
  if (values.start && !localDateTime(values.date, values.start, values.timezone)) {
    return 'The start time does not exist in this timezone. Choose another time.';
  }
  const endDate = values.endDate || values.date;
  if (values.end && !localDateTime(endDate, values.end, values.timezone)) {
    return 'Enter a valid end date and time for this timezone.';
  }
  if (values.start && values.end) {
    const start = localDateTime(values.date, values.start, values.timezone)!;
    const end = localDateTime(endDate, values.end, values.timezone)!;
    if (end.toMillis() < start.toMillis()) return 'The appointment must end after it starts.';
  }
  return null;
}

export function appointmentPatch(values: AppointmentValues): LadingPatch {
  const validation = appointmentValidation(values);
  if (validation) throw new Error(validation);

  return {
    appointment_date: values.date ? `${values.date} 00:00:00` : null,
    appointment_start: appointmentTimestamp(values.date, values.start, values.timezone),
    appointment_end: appointmentTimestamp(values.endDate || values.date, values.end, values.timezone),
    appointment_timezone: values.timezone,
  };
}

function appointmentTimestamp(date: string, time: string, timezone: string): string | null {
  if (!date || !time) return null;
  return localDateTime(date, time, timezone)!
    .setZone(APPOINTMENT_STORAGE_ZONE)
    .toFormat('yyyy-MM-dd HH:mm:ss');
}

export function statusValidation(values: StatusValues): string | null {
  if (!values.status) return 'Choose the new status.';
  if (!localDateTime(values.date, values.time, values.timezone)) {
    return 'Enter a valid event date, time, and timezone.';
  }
  return null;
}

export function patchMatches(lading: LadingInfo, patch: LadingPatch): boolean {
  return (Object.keys(patch) as Array<keyof LadingInfo>).every((field) => {
    const actual = String(lading[field] ?? '');
    const expected = String(patch[field] ?? '');
    if (['appointment_start', 'appointment_end', 'appointment_date'].includes(field)) {
      return actual.replace('T', ' ').slice(0, 19) === expected;
    }
    return actual === expected;
  });
}

export function responseFailure(response: unknown): string | null {
  if (Array.isArray(response)) {
    for (const item of response) {
      const failure = responseFailure(item);
      if (failure) return failure;
    }
    return null;
  }
  if (!response || typeof response !== 'object') return null;
  const value = response as Record<string, unknown>;
  if (value['success'] === false || value['error'] || Number(value['code']) >= 400) {
    return String(value['message'] || value['error'] || 'The server rejected this change.');
  }
  return null;
}
